// components/common/FileViewer.js
import React, { useState, useMemo } from 'react';
import { formatFileSize } from '../../services/fileUploadService';

/**
 * FileViewer component for displaying uploaded files in disputes
 */
const FileViewer = ({ fileMetadata, className = "" }) => {
  // Hooks must run unconditionally
  const [showPreview, setShowPreview] = useState(false);
  const [zoom, setZoom] = useState(1);

  // Use safe defaults so hooks aren't conditional on props
  const meta = fileMetadata || {};
  const { fileName = '', fileSize = 0, fileType = '', downloadURL = '', uploadStatus, uploadError } = meta;

  // Get file extension for icon
  const getFileIcon = (fileType, fileName) => {
    const extension = fileName?.split('.').pop()?.toLowerCase();
    
    if (fileType?.includes('pdf') || extension === 'pdf') return '📄';
    if (fileType?.includes('image') || ['jpg', 'jpeg', 'png'].includes(extension)) return '🖼️';
    if (fileType?.includes('word') || ['doc', 'docx'].includes(extension)) return '📝';
    if (fileType?.includes('text') || extension === 'txt') return '📋';
    return '📎';
  };

  const handleDownload = () => {
    if (downloadURL) {
      window.open(downloadURL, '_blank');
    } else {
      alert('File download not available');
    }
  };

  const handlePreview = () => {
    if (downloadURL) {
      // Open inline preview modal for supported types; fallback to new tab
      if (fileType?.includes('image') || fileType?.includes('pdf') || /\.(doc|docx|txt)$/i.test(fileName || '')) {
        setZoom(1);
        setShowPreview(true);
      } else {
        window.open(downloadURL, '_blank');
      }
    }
  };

  // Decide viewer mode and URL
  const viewerConfig = useMemo(() => {
    const extension = (fileName?.split('.')?.pop() || '').toLowerCase();
    const isImage = fileType?.includes('image') || ['jpg','jpeg','png','gif','webp'].includes(extension);
    const isPdf = fileType?.includes('pdf') || extension === 'pdf';
    const isText = fileType?.includes('text') || extension === 'txt';
    const isOffice = ['doc','docx','ppt','pptx','xls','xlsx'].includes(extension) || /msword|officedocument|vnd\.openxmlformats/.test(fileType || '');

    // Google Docs Viewer for Office files (best-effort; may require public URL)
    const googleViewerUrl = downloadURL ? `https://docs.google.com/gview?embedded=1&url=${encodeURIComponent(downloadURL)}` : null;

    return {
      isImage, isPdf, isText, isOffice,
      iframeSrc: isOffice ? googleViewerUrl : downloadURL
    };
  }, [downloadURL, fileName, fileType]);

  // Early return after hooks are declared
  if (!fileMetadata) return null;

  return (
    <div className={`border border-gray-200 rounded-lg p-3 bg-white ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3 flex-1">
          <div className="text-2xl">
            {getFileIcon(fileType, fileName)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-gray-900 truncate">
              {fileName || 'Unknown file'}
            </div>
            <div className="text-xs text-gray-500">
              {fileSize ? formatFileSize(fileSize) : 'Unknown size'}
              {fileType && ` • ${fileType}`}
            </div>
            <div className="flex items-center mt-1">
              {uploadStatus === 'uploaded' && (
                <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded flex items-center">
                  ✓ Uploaded
                </span>
              )}
              {uploadStatus === 'failed' && (
                <span className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded flex items-center">
                  ✗ Upload Failed
                </span>
              )}
              {uploadStatus === 'pending' && (
                <span className="text-xs text-orange-600 bg-orange-100 px-2 py-1 rounded flex items-center">
                  ⏳ Upload Pending
                </span>
              )}
            </div>
            {uploadError && (
              <div className="text-xs text-red-600 mt-1">
                Error: {uploadError}
              </div>
            )}
          </div>
        </div>
        
        {/* Action buttons */}
        <div className="flex items-center space-x-2">
          {downloadURL && uploadStatus === 'uploaded' && (
            <>
              {(fileType?.includes('image') || fileType?.includes('pdf') || /\.(doc|docx|txt)$/i.test(fileName || '')) && (
                <button
                  onClick={handlePreview}
                  className="text-blue-600 hover:text-blue-800 text-xs bg-blue-100 hover:bg-blue-200 px-2 py-1 rounded transition-colors"
                  title="Preview file"
                >
                  👁️ Preview
                </button>
              )}
              <button
                onClick={handleDownload}
                className="text-green-600 hover:text-green-800 text-xs bg-green-100 hover:bg-green-200 px-2 py-1 rounded transition-colors"
                title="Download file"
              >
                ⬇️ Download
              </button>
            </>
          )}
          {uploadStatus !== 'uploaded' && (
            <span className="text-xs text-gray-500">
              File not available
            </span>
          )}
        </div>
      </div>

      {/* Inline Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60">
          <div className="bg-white rounded-lg shadow-xl w-[95vw] max-w-5xl max-h-[90vh] flex flex-col">
            <div className="px-4 py-2 border-b flex items-center justify-between">
              <div className="min-w-0 pr-2">
                <div className="text-sm font-semibold text-gray-900 truncate">{fileName}</div>
                <div className="text-xs text-gray-500 truncate">{fileType || 'Unknown type'} • {fileSize ? formatFileSize(fileSize) : 'Unknown size'}</div>
              </div>
              <div className="flex items-center gap-2">
                {/* Zoom controls (images and PDFs) */}
                {(viewerConfig.isImage || viewerConfig.isPdf) && (
                  <div className="flex items-center gap-1 mr-2">
                    <button onClick={() => setZoom(prev => Math.max(0.25, Math.round((prev - 0.1) * 100) / 100))} className="text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 px-2 py-1 rounded" title="Zoom out">-</button>
                    <div className="text-xs w-12 text-center">{Math.round(zoom * 100)}%</div>
                    <button onClick={() => setZoom(prev => Math.min(4, Math.round((prev + 0.1) * 100) / 100))} className="text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 px-2 py-1 rounded" title="Zoom in">+</button>
                    <button onClick={() => setZoom(1)} className="text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 px-2 py-1 rounded" title="Reset zoom">Fit</button>
                  </div>
                )}
                <button onClick={() => window.open(downloadURL, '_blank')} className="text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 px-2 py-1 rounded">↗ Open in new tab</button>
                <button onClick={() => setShowPreview(false)} className="text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 px-2 py-1 rounded">✖ Close</button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-gray-100">
              {/* Image Preview */}
              {viewerConfig.isImage && (
                <div className="w-full h-full flex items-center justify-center p-4">
                  <img src={downloadURL} alt={fileName} style={{ transform: `scale(${zoom})`, transformOrigin: 'center center' }} className="max-h-[80vh] max-w-full object-contain" />
                </div>
              )}

              {/* PDF Preview */}
              {viewerConfig.isPdf && (
                <iframe
                  title="PDF Preview"
                  src={`${viewerConfig.iframeSrc}#zoom=${Math.round(zoom*100)}`}
                  className="w-full h-[80vh]"
                />
              )}

              {/* Text Preview */}
              {viewerConfig.isText && (
                <iframe
                  title="Text Preview"
                  src={viewerConfig.iframeSrc}
                  className="w-full h-[80vh] bg-white"
                />
              )}

              {/* Office Docs Preview via Google Viewer (best-effort) */}
              {viewerConfig.isOffice && !viewerConfig.isPdf && !viewerConfig.isImage && (
                viewerConfig.iframeSrc ? (
                  <iframe
                    title="Document Preview"
                    src={viewerConfig.iframeSrc}
                    className="w-full h-[80vh] bg-white"
                  />
                ) : (
                  <div className="p-6 text-center text-sm text-gray-600">
                    Preview not available. Please use Download to view this file.
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * FileList component for displaying multiple files
 */
export const FileList = ({ files, title = "Attached Files", className = "" }) => {
  if (!files || files.length === 0) return null;

  return (
    <div className={className}>
      <h6 className="text-sm font-medium text-gray-900 mb-2">{title}</h6>
      <div className="space-y-2">
        {files.map((fileMetadata, index) => (
          <FileViewer 
            key={index} 
            fileMetadata={fileMetadata} 
          />
        ))}
      </div>
    </div>
  );
};

export default FileViewer;
